import { ACTIVE_CATALOG } from "./market.js";
import type { ActiveCity } from "./market.js";
import { ARCHETYPES, PACK_LEADER } from "./archetypes.js";
import { COORDS, project } from "./cities.js";
import { AXES, UNI } from "./constants.js";
import { emojiFor } from "./media.js";
import { personaFromTally, personaSim, typeFromPersona } from "./persona.js";
import { routeUser } from "./routing.js";
import { expScore, monthOk } from "./scoring.js";
import { ACTIVE_SIDEKICKS } from "./market.js";
import { excludedBy, type Exclusion, type PartyConstraints } from "./suitability.js";
import type {
  Archetype,
  Axes,
  Card,
  CityPlan,
  MapPoint,
  MbtiLetter,
  Profile,
  ScoredExperience,
  ScoredSidekick,
  SwipeEntry,
  Tally,
  Uni,
} from "./types.js";

export interface ProfileInput {
  tally: Tally;
  uni: Uni;
  axes: Axes;
  kids: boolean;
  likes: Card[];
  tripMonth: number;
  tripDays: number;
  log: SwipeEntry[];
  /**
   * Set when the traveller has already booked flights. The city is then a
   * given and only the theme is chosen — no point recommending a city
   * someone cannot change.
   */
  lockedCity?: ActiveCity;
  /**
   * Party constraints derived from the guide data. Omitted in the parity
   * test so the original engine behaviour stays verifiable; the app always
   * passes it.
   */
  constraints?: PartyConstraints;
}

/**
 * Turns raw swipe evidence into the full read: archetype, route, city plan,
 * matched sidekick, and the observations shown back to the user.
 *
 * Pure — extracted from the App component so it can be tested and reused.
 */
export function buildProfile({
  tally,
  uni,
  axes,
  kids,
  likes,
  tripMonth,
  tripDays,
  log,
  constraints,
  lockedCity,
}: ProfileInput): Profile {
  const excluded: Exclusion[] = [];
  const seenExcluded = new Set<string>();
  const allowed = (c: import("./types.js").Experience): boolean => {
    if (!constraints) return true;
    const x = excludedBy(c, constraints);
    if (!x) return true;
    if (!seenExcluded.has(x.id)) {
      seenExcluded.add(x.id);
      excluded.push(x);
    }
    return false;
  };
  const persona = personaFromTally(tally);
  const travelType = typeFromPersona(persona);
  // derived feature scores for archetypes + chips
  const feat: Record<string, number> = {
    ...uni,
    nature: Math.max(0, -axes.terrain.lean) * axes.terrain.care,
    city: Math.max(0, axes.terrain.lean) * axes.terrain.care,
    ancient: Math.max(0, -axes.era.lean) * axes.era.care,
    modern: Math.max(0, axes.era.lean) * axes.era.care,
    hidden: Math.max(0, -axes.fame.lean) * axes.fame.care,
    iconic: Math.max(0, axes.fame.lean) * axes.fame.care,
    serene: Math.max(0, -axes.energy.lean) * axes.energy.care,
    electric: Math.max(0, axes.energy.lean) * axes.energy.care,
    texture: Math.max(0, -axes.lens.lean) * axes.lens.care,
    spectacle: Math.max(0, axes.lens.lean) * axes.lens.care,
  };
  let arch: Archetype = ARCHETYPES[0]!;
  let best = -1;
  ARCHETYPES.forEach((a) => {
    const s = (a.k ?? []).reduce((acc, k) => acc + (feat[k] || 0), 0);
    if (s > best) {
      best = s;
      arch = a;
    }
  });
  if (kids) arch = PACK_LEADER;
  const contrastSeeker = AXES.some(
    ({ key }) => axes[key].care > 0.45 && Math.abs(axes[key].lean) < 0.25,
  );
  // route + experiences
  const route = routeUser(uni, axes, kids, lockedCity, likes);
  // A theme is one city, so the whole trip is spent there.
  const nCities = 1;
  const iconicBias = tripDays <= 8 ? 0.35 : 0;
  const likedIds = new Set<string>(likes.map((c) => c.id));
  const cityPlan: CityPlan[] = route.cities
    .slice(0, nCities)
    .map((city) => {
      const exps = ACTIVE_CATALOG.filter(
        (c) =>
          c.city === city &&
          monthOk(c.months, tripMonth) &&
          (!kids || c.family >= 0.15) &&
          allowed(c),
      )
        .map((c): ScoredExperience => ({
          ...c,
          s: expScore(c, uni, axes) + iconicBias * c.ax.fame + (likedIds.has(c.id) ? 2 : 0),
        }))
        .sort((a, b) => b.s - a.s)
        .slice(0, 2);
      return { city, exps };
    })
    .filter((cp) => cp.exps.length > 0);
  const per = Math.max(1, tripDays);
  const zoneNote = cityPlan.some((cp) =>
    cp.exps.some((e) => ["港澳", "台湾", "西藏"].includes(e.region)),
  );
  // sidekick
  const maxU = Math.max(0.001, ...UNI.map(({ key }) => uni[key]));
  // A sidekick must be local to the city being planned. Matching purely on
  // taste once paired a Shenzhen guide with a Beijing itinerary, which breaks
  // the one thing the product promises.
  const local = ACTIVE_SIDEKICKS.filter((s) => s.city === route.city);
  const bench = local.length ? local : ACTIVE_SIDEKICKS;
  const scored: ScoredSidekick[] = bench
    .map((s): ScoredSidekick => {
      let interest = 0;
      UNI.forEach(({ key }) => {
        interest += uni[key] * s.u[key];
      });
      interest = Math.min(1, interest / (maxU * 2.4));
      let axisFit = 0,
        careSum = 0;
      AXES.forEach(({ key }) => {
        const { lean, care } = axes[key];
        axisFit += care * (1 - Math.abs(s.ax[key] - lean) / 2);
        careSum += care;
      });
      axisFit = careSum > 0 ? axisFit / careSum : 0.5;
      const pFit = personaSim(persona, s.persona);
      const kidBoost = kids ? s.family * 0.15 : 0;
      return {
        ...s,
        type: typeFromPersona(s.persona),
        pFit,
        match: 0.4 * interest + 0.25 * axisFit + 0.3 * pFit + kidBoost,
      };
    })
    .sort((a, b) => b.match - a.match);
  const sidekick = scored[0]!;
  const shared = [0, 1, 2, 3].filter((i) => travelType[i] === sidekick.type[i]).length;
  // --- playful observations generated from actual behavior ---
  const quips: string[] = [];
  const rights = log.filter((e) => e.dir === "right");
  if (rights.length) {
    const fastest = rights.reduce((x, y) => (x.ms < y.ms ? x : y));
    if (fastest.ms >= 400 && fastest.ms < 3000)
      quips.push(
        `"${fastest.card.en}" took you ${(fastest.ms / 1000).toFixed(1)}s. Fastest yes of the night. We saw that.`,
      );
  }
  let streak = 0,
    maxStreak = 0;
  log.forEach((e) => {
    streak = e.dir === "left" ? streak + 1 : 0;
    maxStreak = Math.max(maxStreak, streak);
  });
  if (maxStreak >= 4)
    quips.push(
      `At one point you passed ${maxStreak} cards in a row. Rough neighborhood. We adjusted.`,
    );
  const deadAxis = AXES.find((a) => axes[a.key].care < 0.12);
  if (deadAxis)
    quips.push(
      `${deadAxis.labels[0]} vs ${deadAxis.labels[1]}? You genuinely shrugged. Freeing, honestly.`,
    );
  if (contrastSeeker) quips.push("Misty mountains AND neon skylines. Greedy. We respect it.");
  const PAIRQUIPS: [MbtiLetter, MbtiLetter, string][] = [
    ["F", "T", "People AND views — you refuse to pick. Correct answer."],
    ["E", "I", "Big nights and quiet mornings — you contain multitudes."],
    ["N", "S", "You download the map, then ignore it. Perfect."],
    ["J", "P", "You book the restaurant, then follow your nose anyway."],
  ];
  const pair = PAIRQUIPS.find(([x, y]) => tally[x] >= 1 && tally[y] >= 1);
  if (pair) quips.push(pair[2]);
  const UNIJOKES: Record<string, string> = {
    cul: "Your itinerary is now legally required to be edible.",
    nig: "Daylight hours: for recovery, apparently.",
    adv: "Handrails have been marked optional.",
    cra: "You don't want to watch the tea ceremony — *you want to pour*.",
    sho: "Luggage space: a known casualty.",
  };
  UNI.filter((u) => uni[u.key] >= 0.85)
    .slice(0, 1)
    .forEach((u) => quips.push(UNIJOKES[u.key]!));
  // --- why this sidekick ---
  const reasons: string[] = [];
  if (uni.cul >= 0.5 && sidekick.u.cul >= 0.7)
    reasons.push("You both plan days around meals, not the other way round.");
  if (persona.E >= 0.6 && sidekick.persona.E >= 0.6)
    reasons.push(
      "Two social batteries that never die. The night sections of this trip just doubled.",
    );
  if (persona.E <= 0.4 && sidekick.persona.E <= 0.4)
    reasons.push("Neither of you needs small talk before the first tea.");
  if (persona.J <= 0.4 && sidekick.persona.J <= 0.5)
    reasons.push("Neither of you has ever booked a table a week ahead. It will be fine.");
  if (persona.J >= 0.6 && sidekick.persona.J >= 0.6)
    reasons.push("You both already have the spreadsheet open. Respect.");
  if (kids && sidekick.family >= 0.7)
    // Was hard-coded to Chengdu's panda line, which then appeared under a
    // Shenzhen sidekick. Reasons must hold for whoever is matched.
    reasons.push(
      `${sidekick.name.split(" ")[0]} plans around a small child without making the whole trip about one.`,
    );
  if (reasons.length < 2)
    reasons.push(`${shared}/4 personality letters in common — the arguing will be minimal.`);
  // --- map points ---
  const mapPts: MapPoint[] = cityPlan
    .filter((cp) => COORDS[cp.city])
    .map((cp) => ({
      city: cp.city,
      xy: project(COORDS[cp.city]!).map((v) => Math.round(v)),
      emoji: emojiFor(cp.exps[0]!),
      d: per,
    }));
  const signals = log.length * 10;
  return {
    travelType,
    arch,
    contrastSeeker,
    route,
    cityPlan,
    per,
    zoneNote,
    sidekick,
    shared,
    quips: quips.slice(0, 3),
    reasons: reasons.slice(0, 3),
    mapPts,
    signals,
    excluded,
    matchPct: Math.round(56 + 42 * sidekick.match),
  };
}
